/**
 * ============================================================================
 * AuditDetail — Phase 7 frontend
 * Owner: Phase 7 — Internal Audit Programme Module.
 * Route: /internal-audits/$auditId
 * Purpose: Single-audit detail view: meta-data + findings list + disposition
 *          UI + CAPA linkage panel + history timeline.
 *
 * INA-005 disposition UI:
 *   - Each PENDING finding shows a disposition form with three actions:
 *     "Open CAPA" (→ CAPA_OPENED), "No action" (→ NO_ACTION_REQUIRED,
 *     ≥ 30-char rationale required client-side AND server-side), and
 *     "Withdraw" (audit-owner curator only).
 *   - Findings already disposed display the CAPA back-link or rationale.
 *
 * Cross-module links:
 *   - CAPA id → /capa/$capaId (rendered as a stable link when present)
 *   - Evidence doc ids → document-svc (link out shown to user; deferred to
 *     parent router-level handler).
 *
 * URS rows: INA-003, INA-005, INA-006, CAPA-016.
 * ============================================================================ */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './AuditDetail.module.css';
import type { FindingItem } from '../AuditCalendar/types';
import { useAuditDetailQuery } from './useAuditDetailQuery';
import { useDisposeFindingMutation } from './useDisposeFindingMutation';
import { CostAttributionPanel } from '../Finance/CostAttribution';
import { RelatedRecords } from '../../components/RelatedRecords';

export interface AuditDetailProps {
  auditId: string;
  /** Optional host-injected CAPA navigation (router-agnostic). */
  onNavigateCapa?: (capaId: string) => void;
  /** Optional host-injected document navigation. */
  onNavigateEvidence?: (docId: string) => void;
}

export function AuditDetail({
  auditId,
  onNavigateCapa,
  onNavigateEvidence,
}: AuditDetailProps) {
  const { t } = useTranslation();
  const q = useAuditDetailQuery(auditId);
  const data = q.data;

  if (q.isLoading) {
    return (
      <div className={styles.screen} role="status" aria-live="polite">
        {[0, 1, 2].map((i) => (
          <div key={i} className={styles.skeletonRow} />
        ))}
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className={styles.screen}>
        <div className={styles.empty} role="alert">
          {t('internalAudit.detail.error', {
            defaultValue: 'Could not load audit detail.',
          })}
          <div>
            <button type="button" onClick={() => q.refetch()}>
              {t('common.retry', { defaultValue: 'Retry' })}
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className={styles.screen}>
        <div className={styles.empty}>
          {t('internalAudit.detail.notFound', {
            defaultValue: 'Audit not found.',
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.screen} data-testid="audit-detail-screen">
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>
            {data.audit_number} — {data.audit_scope}
          </h1>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span
              className={styles.dispositionPill}
              data-disposition={statusToPill(data.status)}
              data-testid="ina-audit-status"
            >
              {data.status}
            </span>
            <span style={{ color: 'var(--color-text-muted)' }}>
              {t('internalAudit.detail.kind', { defaultValue: 'Kind' })}: {data.audit_kind}
            </span>
          </div>
        </div>
      </header>

      {/* INA-001 variance banner */}
      {data.variance ? (
        <div className={styles.varianceBanner} role="alert" data-testid="ina-variance-banner">
          {t('internalAudit.detail.variance', {
            defaultValue:
              'Plan-vs-actual variance detected — audit started after planned_end_utc (INA-001).',
          })}
        </div>
      ) : null}

      {/* Meta grid */}
      <section className={styles.metaGrid}>
        <MetaCell label={t('internalAudit.meta.plannedStart', { defaultValue: 'Planned start' })} value={data.planned_start_utc} />
        <MetaCell label={t('internalAudit.meta.plannedEnd', { defaultValue: 'Planned end' })} value={data.planned_end_utc} />
        <MetaCell label={t('internalAudit.meta.actualStart', { defaultValue: 'Actual start' })} value={data.actual_start_utc ?? '—'} />
        <MetaCell label={t('internalAudit.meta.actualEnd', { defaultValue: 'Actual end' })} value={data.actual_end_utc ?? '—'} />
        <MetaCell label={t('internalAudit.meta.leadAuditor', { defaultValue: 'Lead auditor' })} value={data.lead_auditor_user_id} />
        <MetaCell label={t('internalAudit.meta.standards', { defaultValue: 'Standards' })} value={(data.standard_refs ?? []).join(', ') || '—'} />
        <MetaCell label={t('internalAudit.meta.team', { defaultValue: 'Team' })} value={(data.audit_team_user_ids ?? []).length + ' members'} />
        <MetaCell label={t('internalAudit.meta.audited', { defaultValue: 'Audited area' })} value={(data.audited_area_user_ids ?? []).length + ' members'} />
        <MetaCell label={t('internalAudit.meta.reportDoc', { defaultValue: 'Report doc' })} value={data.report_doc_id ?? '—'} />
      </section>

      {/* Findings */}
      <section aria-labelledby="ina-findings-heading">
        <h2 id="ina-findings-heading" style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>
          {t('internalAudit.detail.findings', { defaultValue: 'Findings' })} ({data.findings.length})
        </h2>
        {data.findings.length === 0 ? (
          <div className={styles.empty}>
            {t('internalAudit.detail.findingsEmpty', {
              defaultValue: 'No findings yet for this audit.',
            })}
          </div>
        ) : (
          <ul className={styles.findingList} data-testid="ina-findings-list">
            {data.findings.map((f) => (
              <FindingCard
                key={f.finding_id}
                auditId={auditId}
                finding={f}
                onNavigateCapa={onNavigateCapa}
                onNavigateEvidence={onNavigateEvidence}
              />
            ))}
          </ul>
        )}
      </section>

      {/* Timeline */}
      <section aria-labelledby="ina-timeline-heading">
        <h2 id="ina-timeline-heading" style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>
          {t('internalAudit.detail.timeline', { defaultValue: 'Timeline' })}
        </h2>
        <div className={styles.timeline} data-testid="ina-timeline">
          {data.history.length === 0 ? (
            <div>{t('internalAudit.detail.timelineEmpty', { defaultValue: 'No events yet.' })}</div>
          ) : (
            data.history.map((h) => (
              <div key={h.history_id} className={styles.timelineRow}>
                <span>{h.event_at_utc}</span>
                <span>
                  {h.event_code}{' '}
                  {h.actor_user_id ? <em style={{ color: 'var(--color-text-muted)' }}>by {h.actor_user_id}</em> : null}
                </span>
              </div>
            ))
          )}
        </div>
      </section>

      {/* C-4c — Related Records (link this audit to deviations / CAPAs / OOS / …). */}
      <RelatedRecords entityType="AUDIT" entityId={auditId} />

      {/* Finance module — Cost Attribution (v0.13 Tier 1.2). */}
      <CostAttributionPanel
        subjectEntityType="AUDIT_FINDING"
        subjectEntityId={auditId}
        subjectDisplayLabel={`${data.audit_number} — ${data.audit_scope}`}
      />
    </div>
  );
}

interface FindingCardProps {
  auditId: string;
  finding: FindingItem;
  onNavigateCapa?: (capaId: string) => void;
  onNavigateEvidence?: (docId: string) => void;
}

function FindingCard({ auditId, finding, onNavigateCapa, onNavigateEvidence }: FindingCardProps) {
  const { t } = useTranslation();
  const [rationale, setRationale] = useState('');
  const [responseSummary, setResponseSummary] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const mut = useDisposeFindingMutation();

  const isPending = finding.disposition === 'PENDING';
  const rationaleTooShort = rationale.trim().length < 30;

  function submit(disposition: 'CAPA_OPENED' | 'NO_ACTION_REQUIRED' | 'WITHDRAWN') {
    setFormError(null);
    if (disposition === 'NO_ACTION_REQUIRED' && rationaleTooShort) {
      setFormError(
        t('internalAudit.detail.rationaleTooShort', {
          defaultValue:
            'Rationale for NO_ACTION_REQUIRED must be at least 30 characters (INA-005).',
        }),
      );
      return;
    }
    mut.mutate(
      {
        auditId,
        findingId: finding.finding_id,
        disposition,
        noActionRationale: disposition === 'NO_ACTION_REQUIRED' ? rationale.trim() : undefined,
        responseSummary: responseSummary.trim() || undefined,
      },
      {
        onError: (err) => setFormError(err.message),
      },
    );
  }

  return (
    <li
      className={styles.findingCard}
      data-testid={`ina-finding-${finding.finding_number}`}
    >
      <div className={styles.findingHeader}>
        <span className={styles.findingNumber}>{finding.finding_number}</span>
        <span className={styles.classificationPill} data-cls={finding.classification}>
          {finding.classification}
        </span>
        <span className={styles.dispositionPill} data-disposition={finding.disposition}>
          {finding.disposition}
        </span>
      </div>
      <div>
        <strong>{finding.title}</strong>
      </div>
      <div style={{ color: 'var(--color-text-muted)' }}>{finding.description}</div>

      {/* Evidence docs */}
      {finding.evidence_doc_ids && finding.evidence_doc_ids.length > 0 ? (
        <div>
          <span className={styles.metaLabel}>
            {t('internalAudit.detail.evidence', { defaultValue: 'Evidence' })}:
          </span>{' '}
          {finding.evidence_doc_ids.map((d) => (
            <button
              key={d}
              type="button"
              className={styles.capaLink}
              onClick={() => onNavigateEvidence?.(d)}
              data-testid={`ina-evidence-${d}`}
            >
              {d}
            </button>
          ))}
        </div>
      ) : null}

      {/* CAPA linkage panel */}
      {finding.capa_id ? (
        <div data-testid={`ina-capa-link-${finding.finding_number}`}>
          <span className={styles.metaLabel}>
            {t('internalAudit.detail.capaLink', { defaultValue: 'CAPA' })}:{' '}
          </span>
          <button
            type="button"
            className={styles.capaLink}
            onClick={() => onNavigateCapa?.(finding.capa_id!)}
          >
            {finding.capa_id}
          </button>
        </div>
      ) : null}

      {/* No-action rationale display */}
      {finding.no_action_rationale ? (
        <div>
          <span className={styles.metaLabel}>
            {t('internalAudit.detail.noActionRationale', { defaultValue: 'No-action rationale' })}:
          </span>{' '}
          <span>{finding.no_action_rationale}</span>
        </div>
      ) : null}

      {/* Disposition form — only for PENDING */}
      {isPending ? (
        <div className={styles.dispositionForm}>
          <label htmlFor={`rationale-${finding.finding_id}`} className={styles.metaLabel}>
            {t('internalAudit.detail.rationaleLabel', {
              defaultValue: 'No-action rationale (≥30 chars, only needed for NO_ACTION_REQUIRED)',
            })}
          </label>
          <textarea
            id={`rationale-${finding.finding_id}`}
            rows={3}
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            data-testid={`ina-rationale-${finding.finding_number}`}
            aria-describedby={`rationale-hint-${finding.finding_id}`}
          />
          <small
            id={`rationale-hint-${finding.finding_id}`}
            style={{
              color: rationaleTooShort ? 'var(--color-warning-fg, #92400e)' : 'var(--color-text-muted)',
            }}
          >
            {rationale.trim().length}/30
          </small>
          <label htmlFor={`response-${finding.finding_id}`} className={styles.metaLabel}>
            {t('internalAudit.detail.responseLabel', {
              defaultValue: 'Auditee response summary (optional)',
            })}
          </label>
          <textarea
            id={`response-${finding.finding_id}`}
            rows={2}
            value={responseSummary}
            onChange={(e) => setResponseSummary(e.target.value)}
            data-testid={`ina-response-${finding.finding_number}`}
          />
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => submit('CAPA_OPENED')}
              disabled={mut.isPending}
              data-testid={`ina-capa-btn-${finding.finding_number}`}
              style={{
                padding: '0.5rem 0.75rem',
                background: 'var(--color-primary, #1d4ed8)',
                color: '#fff',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
              }}
            >
              {t('internalAudit.detail.openCapa', { defaultValue: 'Open CAPA' })}
            </button>
            <button
              type="button"
              onClick={() => submit('NO_ACTION_REQUIRED')}
              disabled={mut.isPending || rationaleTooShort}
              data-testid={`ina-no-action-btn-${finding.finding_number}`}
              style={{
                padding: '0.5rem 0.75rem',
                background: 'transparent',
                color: 'var(--color-text-default)',
                border: '1px solid var(--color-border-default, #d1d5db)',
                borderRadius: '0.375rem',
                cursor: 'pointer',
              }}
            >
              {t('internalAudit.detail.noAction', { defaultValue: 'No action required' })}
            </button>
            <button
              type="button"
              onClick={() => submit('WITHDRAWN')}
              disabled={mut.isPending}
              data-testid={`ina-withdraw-btn-${finding.finding_number}`}
              style={{
                padding: '0.5rem 0.75rem',
                background: 'transparent',
                color: 'var(--color-text-muted, #6b7280)',
                border: '1px solid var(--color-border-subtle, #e5e7eb)',
                borderRadius: '0.375rem',
                cursor: 'pointer',
              }}
            >
              {t('internalAudit.detail.withdraw', { defaultValue: 'Withdraw' })}
            </button>
          </div>
          {formError ? (
            <div className={styles.errorText} role="alert">
              {formError}
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function MetaCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className={styles.metaLabel}>{label}</div>
      <div className={styles.metaValue}>{value}</div>
    </div>
  );
}

function statusToPill(s: string): string {
  // Reuse disposition palette for the audit-level pill so colours stay consistent.
  switch (s) {
    case 'PLANNED':
      return 'PENDING';
    case 'CLOSED':
      return 'NO_ACTION_REQUIRED';
    case 'CANCELLED':
      return 'WITHDRAWN';
    default:
      return 'CAPA_OPENED';
  }
}
